import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  kycGotoPrevPage as kycGotoPrevPageRoutine,
  kycSubmitCustomerDetails as kycSubmitCustomerDetailsRoutine,
} from 'spa/actions/VerificationActions';
import VerificationConstants from 'spa/constants/VerificationConstants';
import { Alpha3ToAlpha2 } from 'spa/constants/ISOCountryCodes';
import {
  customerIsCompanySelector,
  customerKycSubmitLoadingSelector,
  customerKyc1VerifiedSelector,
  buyerCheckoutDetailsSelector,
  customerKyc2ApprovedSelector,
  customerKycHasPrevPagesSelector,
  customerSelector,
} from 'spa/selectors/CustomerSelectors';
import { draftBuyerDetailsSelector } from 'spa/selectors/TransactionSelectors';
import ActionButtons from 'spa/components/CustomerVerification/V2/ActionButtons';
import { KYCContext } from 'spa/context/KYCContext';
import { AccountTypeSwitcher } from './Fields';
import IndividualDetailsFields from './IndividualDetailsFields';

const IndividualDetailsForm = (props) => {
  const {
    loadPrevPage,
    submitIndividualDetails,
    isCompany,
    isSubmitting,
    hasContactDetails,
    customerFieldValues,
    hasSubmittedVat,
    hasPrevPages,
    isPersonalInformationVerified,
    occupationOptions,
    occupationId,
  } = props;
  const [isEditting, setIsEditting] = useState(hasContactDetails);
  useEffect(() => {
    if (!isSubmitting) {
      setIsEditting(hasContactDetails);
    }
  }, [hasContactDetails, isSubmitting]);

  const kycContext = useContext(KYCContext);
  const { forceCountry } = kycContext;

  const defaultCountry =
    forceCountry ||
    (Alpha3ToAlpha2[window.config.geoip_country] &&
      Alpha3ToAlpha2[window.config.geoip_country].toLowerCase()) ||
    'us';
  const [showAutocompleteAddressError, setShowAutocompleteAddressError] = useState(false);

  const onFormSubmit = (values, form) => {
    submitIndividualDetails({
      values,
      form,
      formName: VerificationConstants.CUSTOMER_DETAILS_FORM,
      kycContext,
    });
  };
  const initalValues = useMemo(
    () => ({
      'account-type': isCompany
        ? VerificationConstants.ACCOUNT_TYPE_COMPANY
        : VerificationConstants.ACCOUNT_TYPE_INDIVIDUAL,
      occupation_id: occupationId || null,
      ...customerFieldValues,
    }),
    [customerFieldValues, isCompany, occupationId]
  );
  return (
    <Form
      onSubmit={onFormSubmit}
      subscription={{
        values: true,
        hasValidationErrors: true,
      }}
      initialValues={initalValues}
      mutators={{
        clearAutocompleteAddressError: ([prefix], state) => {
          state.fields[`${prefix}address`].data.error = undefined;
          state.fields[`${prefix}city`].data.error = undefined;
          state.fields[`${prefix}state`].data.error = undefined;
        },
      }}
    >
      {({ handleSubmit, hasValidationErrors, values }) => (
        <form onSubmit={handleSubmit} name={VerificationConstants.CUSTOMER_DETAILS_FORM}>
          {!isEditting && (
            <AccountTypeSwitcher noModal isCompany={isCompany} disabled={isSubmitting} />
          )}
          <IndividualDetailsFields
            values={values}
            defaultCountry={defaultCountry}
            lockCountry={!!forceCountry}
            disabled={isSubmitting}
            isPersonalInformationVerified={isPersonalInformationVerified}
            customerFieldValues={customerFieldValues}
            hasSubmittedVat={hasSubmittedVat}
            occupationOptions={occupationOptions}
            showAutocompleteAddressError={showAutocompleteAddressError}
            setShowAutocompleteAddressError={setShowAutocompleteAddressError}
          />
          <ActionButtons
            secondaryText="Back"
            primaryText="Save and next"
            isLoading={isSubmitting}
            isDisabled={hasValidationErrors}
            type="submit"
            withBack={hasPrevPages}
            onSecondaryClick={() => loadPrevPage()}
            onPrimaryClick={() => {
              setShowAutocompleteAddressError(true);
            }}
          />
        </form>
      )}
    </Form>
  );
};

const mapStateToProps = (state) => {
  const hasContactDetails = customerKyc1VerifiedSelector(state);
  const buyerDetails =
    buyerCheckoutDetailsSelector(state) || draftBuyerDetailsSelector(state) || {};
  return {
    customerFieldValues: buyerDetails,
    hasSubmittedVat: !!buyerDetails['vat-number'],
    hasContactDetails,
    isCompany: customerIsCompanySelector(state),
    isSubmitting: customerKycSubmitLoadingSelector(state),
    isPersonalInformationVerified: customerKyc2ApprovedSelector(state),
    hasPrevPages: customerKycHasPrevPagesSelector(state),
    occupationId: customerSelector(state).occupation_id,
  };
};

const mapDispatchToProps = (dispatch) => ({
  loadPrevPage: () => dispatch(kycGotoPrevPageRoutine.trigger()),
  submitIndividualDetails: (payload) => dispatch(kycSubmitCustomerDetailsRoutine.trigger(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndividualDetailsForm);
